import { FC } from 'react'
import Link from 'next/link'
import { useTranslation } from 'next-i18next/pages'
import { Button } from '@corratech/pylot-ui'
import { useUI, ToastType } from '@corratech/pylot-ui/context'
import { ProductPrice } from '@components/common/ProductPrice/ProductPrice'
import { AddToWishlist } from '../../AddToWishlist'
import { useAddToCart } from '@pylot-data/hooks/cartManager/use-add-to-cart'
import { isMobileScreen } from '@lib/utils/mediaQuery'
import { cartUpdateEvent } from '@lib/utils/exponeaTrackingEvents'
import {
    ConfigurableProduct,
    WishlistSharedItems
} from '@pylot-data/pylotschema'
import { getUidForConfigProducts } from '../../utils/getUidForConfigProducts'

import cn from 'classnames'
import s from './ShareWishlistItemGrid.module.scss'

export type SharedWishlistItemsProps = {
    item: WishlistSharedItems
}

const renderWishListButtonContent = (t: any) => {
    return function WishlistButtonContent({
        isProductInWishlist
    }: {
        isProductInWishlist: boolean
    }) {
        return (
            <div className={s['add-to-wishlist-btn']}>
                {isProductInWishlist
                    ? t('Agregar a mis favoritos')
                    : t('Lista de favoritos')}
            </div>
        )
    }
}

const TIMEOUT_AFTER_OPENING_SIDEBAR = 150

export const ShareWishlistItem: FC<SharedWishlistItemsProps> = ({ item }) => {
    const { t } = useTranslation(['wishlist', 'common'])
    const { addToCart, isAdding } = useAddToCart()
    const isMobile = isMobileScreen()
    const { openToast, openSidebar, openCartToast } = useUI()

    const productName = item?.product?.name
    const rawImage = item?.product.small_image.url
    const productImage =
        rawImage && rawImage.startsWith('//') ? `https:${rawImage}` : rawImage
    const productPrice = item?.product.price_range
    const isConfig = item?.product.__typename === 'ConfigurableProduct'
    const imageHeight = isMobile ? 150 : 200

    const productUrl = `/producto/${item?.product.url_key}`

    const addProductHandler = async () => {
        const product = item.product as unknown as ConfigurableProduct & {
            uid?: string
        }
        if (!product.sku) {
            return
        }
        const configurableOptions = item.share_configurable_options?.map(
            (o) => ({
                value_id: Number(o.value_id)
            })
        )
        const selected_options =
            isConfig && configurableOptions?.length
                ? getUidForConfigProducts({
                      product,
                      options: configurableOptions as never
                  })
                : undefined

        const cartLine: {
            sku: string
            quantity: number
            uid?: string
            selected_options?: string[]
        } = {
            sku: product.sku,
            quantity: 1
        }
        if (product.uid != null && product.uid !== '') {
            cartLine.uid = product.uid
        }
        if (selected_options?.length) {
            cartLine.selected_options = selected_options
        }

        const response = await addToCart([cartLine], [])

        const errorMessage =
            response?.errors?.[0]?.message ??
            response?.user_errors?.[0]?.message ??
            null

        const successMessage = t('common||cart|{{name}} was added to cart', {
            name: item.product.name
        })

        if (!isMobile) {
            openSidebar()
        }

        if (response?.data && !errorMessage) {
            const { cart } = response.data

            const addedCurrentProduct = {
                ...item.product,
                sku: item.product.sku,
                uid: product.uid ?? `${item.product.id}`
            }
            cartUpdateEvent('add', addedCurrentProduct, cart)
        }

        setTimeout(
            () =>
                isMobile
                    ? openToast(
                          errorMessage ? errorMessage : successMessage,
                          errorMessage ? ToastType.Warning : ToastType.Success,
                          true
                      )
                    : openCartToast(
                          errorMessage ? errorMessage : successMessage,
                          errorMessage ? ToastType.Warning : ToastType.Success
                      ),
            TIMEOUT_AFTER_OPENING_SIDEBAR
        )
    }

    return (
        <div className={s['shared-item-container']}>
            <div className={s['shared-item-info']}>
                <Link
                    href={productUrl}
                    className={cn(s['shared-item-img'])}
                    aria-label={productName}
                    rel="noopener noreferrer"
                >
                    <img
                        src={productImage ? productImage : ''}
                        alt={productName ? productName : 'placeholder'}
                        width={imageHeight}
                        height={imageHeight}
                        className={cn(s['product-img'], 'contain')}
                    />
                </Link>
                <div className={s['shared-item-name']}>
                    <Link href={productUrl}>{productName}</Link>
                    {isMobile && (
                        <ProductPrice
                            className={String(
                                item.product.__typename
                            ).toLowerCase()}
                            priceRange={productPrice}
                            // shouldDisplayPriceRange
                        />
                    )}
                </div>
            </div>
            {!isMobile && (
                <div className={s['shared-item-price']}>
                    <ProductPrice
                        className={String(
                            item.product.__typename
                        ).toLowerCase()}
                        priceRange={productPrice}
                        // shouldDisplayPriceRange
                    />
                </div>
            )}
            <div className={s['shared-item-actions']}>
                <Button
                    aria-label={t('Add to Cart')}
                    type="button"
                    variant="secondary"
                    className={s['shared-item-add']}
                    onClick={() => addProductHandler()}
                    loading={isAdding}
                    disabled={isAdding}
                >
                    {isAdding
                        ? t('wishlist|Adding')
                        : t('wishlist|Add to cart')}
                </Button>
                <AddToWishlist
                    sku={item?.product.sku}
                    // selected_options={selectedOptionUIDs}
                    quantity={1}
                    isWishlistDisabled={false}
                    isConfig={isConfig}
                    enableRemoveProductFromWishlist
                    buttonContent={renderWishListButtonContent(t)}
                    errorWithToast
                    stayOnPage
                />
            </div>
            {/* <div className={s['wishlist-shared-container']}>{itemGrid}</div> */}
        </div>
    )
}
